import {
  type IapOfferCodeMapping,
  type IapOfferCodeMappingCreateRequest,
  type IapOfferCodeMappingUpdateRequest,
  type IapOfferCodeMappingsListQuerySchema,
  type Paginated,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contract's own INPUT shape (U3) — never a hand-written copy of the filters. */
type OfferCodeParams = z.input<typeof IapOfferCodeMappingsListQuerySchema>;

const OFFER_CODES = '/admin/iap-offer-codes';

/**
 * The store offer-code mappings — how an earned discount reaches an IAP price.
 *
 * ## Its own api, beside the catalogue rather than inside it
 *
 * `subscriptionsApi` owns what we sell; this owns how a discount is honoured in
 * a store we do not control. They share a permission (`billing.read` /
 * `billing.manage`) and nothing else: no tag here can invalidate one there, and
 * merging them would put a plan rename and a code edit in one cache for no
 * reason but proximity.
 *
 * ## The names on a row arrive already resolved
 *
 * `discountRuleName` and `productName` are filled in server-side over the
 * QueryBus, in the caller's locale, once per page (N6). So the table needs **no
 * lookup at all** — reaching for one here would be an N+1 for columns the api
 * has already answered. The lookups exist for the *pickers*, which is a
 * different question.
 */
export const iapOfferCodesApi = createApi({
  reducerPath: 'iapOfferCodesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['OfferCodes', 'OfferCode'],
  endpoints: (builder) => ({
    listOfferCodes: builder.query<Paginated<IapOfferCodeMapping>, OfferCodeParams>({
      query: (params) => ({ url: OFFER_CODES, method: 'GET', params }),
      providesTags: ['OfferCodes'],
    }),

    offerCode: builder.query<IapOfferCodeMapping, string>({
      query: (id) => ({ url: `${OFFER_CODES}/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'OfferCode' as const, id }],
    }),

    createOfferCode: builder.mutation<IapOfferCodeMapping, IapOfferCodeMappingCreateRequest>({
      query: (body) => ({ url: OFFER_CODES, method: 'POST', body }),
      invalidatesTags: ['OfferCodes'],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceOfferCodeChange(queryFulfilled.then(({ data }) => data.discountRuleId));
      },
    }),

    updateOfferCode: builder.mutation<
      IapOfferCodeMapping,
      { id: string; body: IapOfferCodeMappingUpdateRequest }
    >({
      query: ({ id, body }) => ({ url: `${OFFER_CODES}/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'OfferCode' as const, id },
        'OfferCodes' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        // Read off the RESPONSE, not the argument: the identity triple is
        // create-only, so a PATCH body never carries the rule this code pays
        // for and only the row the api wrote knows it.
        await announceOfferCodeChange(queryFulfilled.then(({ data }) => data.discountRuleId));
      },
    }),

    deleteOfferCode: builder.mutation<void, string>({
      query: (id) => ({ url: `${OFFER_CODES}/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'OfferCode' as const, id },
        'OfferCodes' as const,
      ],
      async onQueryStarted(_id, { queryFulfilled }) {
        // A delete answers 204 and names nothing — and the argument is the
        // mapping's own id, which is not a rule. `null` says that honestly.
        await announceOfferCodeChange(queryFulfilled.then(() => null));
      },
    }),
  }),
});

/**
 * Announces the change on the client bus (W5).
 *
 * The **rule** is what travels, because that is what a reader re-reads: "what
 * does this rule now pay for in the stores". A mapping id is a row nobody
 * outside this screen is holding.
 */
async function announceOfferCodeChange(landed: Promise<string | null>): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'billing.offerCode.changed', discountRuleId: await landed });
  } catch {
    // Refused — nothing to announce. Swallowed deliberately: `onQueryStarted`
    // has no caller, so a rejection escaping it becomes an unhandled rejection
    // that exits the runner non-zero beside a wholly green suite (P22b1). The
    // failure is already the caller's, through `unwrap()`.
  }
}

export const {
  useListOfferCodesQuery,
  useOfferCodeQuery,
  useCreateOfferCodeMutation,
  useUpdateOfferCodeMutation,
  useDeleteOfferCodeMutation,
} = iapOfferCodesApi;
